import React, { useState } from 'react';
import { FiPaperclip, FiDownload, FiMail, FiCornerUpLeft } from 'react-icons/fi';
import type { InquiryItem } from '../types';
import { triggerFileDownload } from '../../../../utils/download';

interface InquiryDetailPanelProps {
  inquiry: InquiryItem | null;
  onOpenReply?: () => void;
  onOpenSendMail?: () => void;
  onOpenChat?: () => void;
  onOpenUpdateStatus?: () => void;
  onCloseInquiry?: () => void;
  onPickFirst?: () => void;
  conversationReplies?: any[];
  attachments?: { name: string; url: string }[] | null;
}

const statusBadgeStyles: Record<string, string> = {
  new: 'bg-[#1B2B4B] text-[#4A90FF]',
  replied: 'bg-[#1A3A28] text-[#3DD06C]',
  offer_sent: 'bg-[#3A2E1A] text-[#F5A623]',
  closed: 'bg-[#2A2A2D] text-[#8E8E93]',
};

const statusLabels: Record<string, string> = {
  new: 'New Inquiry',
  replied: 'Replied',
  offer_sent: 'Offer Sent',
  closed: 'Closed',
};

export const InquiryDetailPanel: React.FC<InquiryDetailPanelProps> = ({
  inquiry,
  onOpenReply,
  onOpenSendMail,
  onOpenChat,
  onOpenUpdateStatus,
  onCloseInquiry,
  onPickFirst,
  conversationReplies,
  attachments,
}) => {
  const [conversationOpen] = useState(true);

  if (!inquiry) {
    return (
      <div className="flex h-full min-h-[500px] flex-col items-center justify-center rounded-3xl border border-[#2C2C2E] bg-[#1C1C1E] p-8 text-center">
        {/* Card Mockup Illustration with overlapping floating mail badge */}
        <div className="relative mb-6 flex h-24 w-36 items-center justify-center rounded-2xl border border-[#3A3A3C] bg-[#2C2C2E]">
          <div className="w-24 space-y-2">
            <div className="h-2.5 w-16 rounded bg-white/10" />
            <div className="h-2 w-20 rounded bg-white/10" />
          </div>
          <div className="absolute -bottom-3 flex h-10 w-10 items-center justify-center rounded-full border border-[#3A3A3C] bg-[#3A3A3C] text-white shadow-lg">
            <FiMail size={18} />
          </div>
        </div>

        <h3 className="mb-2 text-xl font-bold text-white">No inquiry selected</h3>
        <p className="mb-6 max-w-xs text-sm text-zinc-400">
          Select an inquiry from the list to view details and respond.
        </p>

        {onPickFirst && (
          <button
            onClick={onPickFirst}
            className="inline-flex items-center gap-2 rounded-full border border-[#3A3A3C] bg-[#2C2C2E] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#3A3A3C] cursor-pointer"
          >
            <FiMail size={16} />
            <span>Pick an inquiry to start</span>
          </button>
        )}
      </div>
    );
  }

  const initial = inquiry.guestName.charAt(0).toUpperCase() || 'G';

  const repliesToRender = conversationReplies || [];

  return (
    <div className="flex h-full max-h-full flex-col rounded-3xl border border-[#2C2C2E] bg-[#1C1C1E] p-4 sm:p-6 shadow-2xl overflow-hidden min-h-0">
      {/* ── Top Header (Sender info) ─────────────────────────────────────────── */}
      <div className="flex shrink-0 flex-wrap sm:flex-nowrap items-start justify-between gap-4 border-b border-[#2C2C2E] pb-4 mb-4">
        <div className="flex items-center gap-4 min-w-0">
          {inquiry.applicantAvatar ? (
            <img
              src={inquiry.applicantAvatar}
              className="h-12 w-12 shrink-0 rounded-full object-cover shadow-md"
              alt={inquiry.guestName}
            />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 text-lg font-bold text-white shadow-md">
              {initial}
            </div>
          )}
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-white truncate">{inquiry.guestName}</h2>
            <button
              type="button"
              onClick={onOpenReply || onOpenSendMail}
              className="text-sm font-medium text-[#1784FF] hover:underline truncate block text-left cursor-pointer"
            >
              {inquiry.guestEmail}
            </button>
          </div>
        </div>

        <div className="text-left sm:text-right shrink-0">
          <span
            className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
              statusBadgeStyles[inquiry.status] || 'bg-zinc-800 text-zinc-300'
            }`}
          >
            {statusLabels[inquiry.status] || inquiry.status}
          </span>
          <p className="mt-1 text-xs text-zinc-500">
            Received: {inquiry.receivedAt || inquiry.timestamp || 'Today, 2:45 PM'}
          </p>
        </div>
      </div>

      {/* ── Scrollable Message Content Body ───────────────────────────────────── */}
      <div className="space-y-5 overflow-y-auto custom-modal-scrollbar pr-2 flex-1 min-h-0">
        {/* Interested in */}
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">
            INTERESTED IN
          </p>
          <span className="inline-block rounded-2xl border border-[#3A3A3C] bg-[#2C2C2E] px-4 py-2 text-sm font-semibold text-white">
            {inquiry.interestedIn}
          </span>
        </div>

        {/* Source */}
        {inquiry.source && inquiry.source !== 'Website' && (
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">SOURCE</p>
            <span className="inline-block rounded-2xl border border-[#3A3A3C] bg-[#2C2C2E] px-4 py-2 text-sm font-semibold text-white">
              {inquiry.source}
            </span>
          </div>
        )}

        {/* Message Content */}
        <div className="rounded-2xl border border-[#2C2C2E] bg-[#2A2A2D] p-4 text-sm leading-relaxed text-zinc-200">
          <div className="whitespace-pre-wrap break-words">
            {inquiry.message || 'No description provided.'}
          </div>
        </div>

        {/* Attachments */}
        {attachments && attachments.length > 0 && (
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-wider text-zinc-500">
              ATTACHMENTS
            </p>
            <div className="flex flex-wrap gap-3">
              {attachments.map((att, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerFileDownload(att.url, att.name);
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-[#3A3A3C] bg-[#2C2C2E] px-4 py-2 text-xs font-medium text-white transition hover:bg-[#3A3A3C] cursor-pointer"
                  title="Download file"
                >
                  <FiPaperclip size={14} className="text-zinc-400" />
                  <span className="truncate max-w-[150px]">{att.name}</span>
                  <FiDownload size={14} className="text-[#1784FF]" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Conversation Thread matching Image 1 */}
        <div className="rounded-2xl border border-[#2C2C2E] bg-[#2A2A2D] overflow-hidden">
          <div
            onClick={onOpenChat}
            className="flex w-full items-center justify-between p-4 text-left font-medium text-white transition hover:bg-white/5 cursor-pointer border-b border-[#2C2C2E]/50"
          >
            <div className="flex items-center gap-2">
              <span>Conversation ({repliesToRender.length} replies)</span>
              {repliesToRender.length > 3 && (
                <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-[#1784FF]">
                  Showing last 3
                </span>
              )}
            </div>
            <span className="text-xs text-[#1784FF] font-semibold hover:underline">
              View in Chat &rarr;
            </span>
          </div>

          {conversationOpen && (
            <div
              onClick={onOpenChat}
              className="space-y-3 p-4 cursor-pointer hover:bg-white/[0.02] transition relative group"
            >
              {/* Floating click overlay tooltip on hover */}
              <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-blue-500/[0.01] transition duration-150 flex items-center justify-center pointer-events-none">
                <span className="opacity-0 group-hover:opacity-100 transition duration-150 text-[11px] font-semibold text-white bg-zinc-900 border border-zinc-800 px-2.5 py-1.5 rounded-full shadow-lg">
                  Click to view full thread & reply
                </span>
              </div>

              {repliesToRender.length === 0 ? (
                <p className="text-zinc-500 text-xs text-center py-2">
                  No messages in this conversation yet.
                </p>
              ) : (
                (repliesToRender.length > 3 ? repliesToRender.slice(-3) : repliesToRender).map(
                  (msg) => (
                    <div key={msg.id} className="flex gap-3 text-xs relative z-10">
                      {msg.senderAvatar ? (
                        <img
                          src={msg.senderAvatar}
                          className="h-8 w-8 shrink-0 rounded-full object-cover shadow-sm"
                          alt={msg.senderName}
                        />
                      ) : (
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-bold text-white ${
                            msg.isUser ? 'bg-blue-600' : 'bg-purple-600'
                          }`}
                        >
                          {msg.senderName.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-white">{msg.senderName}</span>
                          <span className="text-zinc-500">{msg.timestamp}</span>
                        </div>
                        {msg.textHtml ? (
                          <div
                            className="text-zinc-300 leading-relaxed break-words line-clamp-3"
                            dangerouslySetInnerHTML={{ __html: msg.textHtml }}
                          />
                        ) : (
                          <p className="text-zinc-300 leading-relaxed line-clamp-3">{msg.text}</p>
                        )}
                      </div>
                    </div>
                  ),
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Sticky Bottom Action Bar matching Image 1 ───────────────────────── */}
      <div className="mt-4 flex shrink-0 items-center justify-between gap-3 border-t border-[#2C2C2E] pt-4 bg-[#1C1C1E]">
        <div className="flex items-center gap-3">
          {/* Reply Button (Triggers Email/Chat Choice) */}
          <button
            onClick={onOpenReply || onOpenSendMail}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 hover:border-white/40 bg-[#2C2C2E] hover:bg-[#3A3A3C] px-5 py-2.5 text-xs sm:text-sm font-semibold text-white transition-all cursor-pointer min-h-[44px]"
          >
            <FiCornerUpLeft size={16} />
            <span>Reply</span>
          </button>
        </div>

        {/* Close Inquiry Action */}
        <button
          type="button"
          onClick={onCloseInquiry || onOpenUpdateStatus}
          className="text-xs sm:text-sm font-medium text-amber-500/90 hover:text-amber-400 transition cursor-pointer"
        >
          Close Inquiry
        </button>
      </div>
    </div>
  );
};
